import React from 'react';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { List, AutoSizer, CellMeasurer,CellMeasurerCache, ListProps,ListRowProps, ListRowRenderer } from 'react-virtualized';
import _ from 'lodash';
import { scrollbarSize } from './scrollbarSize';

const styles = (theme: Theme) =>
  createStyles({
    flexContainer: {
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    },
  });

interface MuiVirtualizedListProps extends WithStyles<typeof styles>, ListProps {
  data: any[];
  customRenderer?: (props:ListRowProps, row: any, measure?: ()=>void, customProps?:any) => React.ReactNode;
  customProps?: any;
  minWidth?: number;
}

class MuiVirtualizedList extends React.PureComponent<MuiVirtualizedListProps> {
  // each list measures its own rows (a shared cache gave one list the row heights of another)
  cache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 102
  });
  list: List | null = null;

  componentDidUpdate(prev: MuiVirtualizedListProps) {
    // new data (new filters, a different page) rather than more of the same: measure again
    const before = prev.data || [];
    const after = this.props.data || [];
    const replaced = after.length < before.length || (before.length > 0 && after.length > 0 && before[0] !== after[0] && (before[0] as any)?._id != (after[0] as any)?._id);
    if(replaced){
      this.cache.clearAll();
      this.list?.recomputeRowHeights();
    }
  }

  setRef = (list: List | null) => {
    this.list = list;
    const {reference} = this.props as any;
    if(typeof reference == 'function')
      reference(list);
    else if(reference)
      reference.current = list;
  }


  renderRow = (rendererProps:ListRowProps) => {
    let {data,customRenderer,customProps} = this.props;
    return (
      <CellMeasurer
        key={rendererProps.key}
        cache={this.cache}
        parent={rendererProps.parent}
        columnIndex={0}
        rowIndex={rendererProps.index}>
        {({registerChild,measure}) => (
          <React.Fragment>
            {/*
            //@ts-ignore*/}
            <div style={rendererProps.style} className="row" ref={registerChild}>
              {customRenderer && customRenderer(rendererProps,data[rendererProps.index],measure,customProps)}
            </div>
          </React.Fragment>
        )}
      </CellMeasurer>
    );
  }

  render() {
    const { classes,reference,data, minWidth, onRowsRendered } = this.props;
    return (
      <AutoSizer>
        {({ height, width }) => {
          // narrower than the list's minimum: scroll sideways inside the space we were given, and leave room for that
          // scrollbar, so the list never grows past its parent (which made the parent scroll and the size flicker)
          const scrollX = !!minWidth && width < minWidth;
          return (
          <div style={{width, height, overflowX: scrollX ? 'auto' : 'hidden', overflowY: 'hidden'}}>
          <List
            // sideways scrolling belongs to the wrapper above; left to itself the list turns it on whenever it has a
            // vertical scrollbar, and rows a little wider than the list then add and remove scrollbars in a loop
            style={{overflowX: 'hidden'}}
            height={scrollX ? Math.max(0, height - scrollbarSize()) : height}
            width={scrollX ? minWidth! : width}
            ref={this.setRef}
            deferredMeasurementCache={this.cache}
            rowHeight={this.cache.rowHeight}
            onRowsRendered={onRowsRendered}
            rowRenderer={this.renderRow}
            rowCount={data.length}
            overscanRowCount={this.props.overscanRowCount ? this.props.overscanRowCount : 10}
            data={data}
          >
          </List>
          </div>
        )}}
      </AutoSizer>
    );
  }
}

export const VirtualizedList = withStyles(styles)(MuiVirtualizedList);